/**
 * BulkBar - Set recruiter and Set status for the ticked rows (docs/plans/crm-recruitment-list-plan.md
 * Part A section 3 step 9, decision 10, B4), the inbox's bulk pattern (pages/Contacts.tsx): one
 * write per row, sequential, never a transaction.
 *
 *   - Set recruiter: THIS study's recruiter per row (recruitLogic buildBulkPlan: PATCH
 *     .../studies/:studyId/owner; on the lead's focus study the server moves their main one).
 *   - Set status: the per-study PATCH /api/crm/contacts/:id/studies/:studyId per row, so no study
 *     confirmation is needed (the study is the sheet). Not interested / Unreachable / Screen failed
 *     / Not eligible ask for the reason ONCE (DispositionReasonModal in its bulk form, which hides
 *     the failed-question picker) and every write carries it.
 *   - A 409 randomized conflict is that row's refusal, reported and never acknowledged in bulk (a
 *     person can still acknowledge it for one row from the status cell). Any failure leaves the
 *     rows before it applied, and the bar says which rows did not change.
 *
 * The plan (which rows get a write, which are already there, whether a reason is needed) and the
 * summary line are recruitLogic's buildBulkPlan / applyBulkReason / bulkRowResult / bulkResultLine.
 * The bar renders nothing without a selection, a run in progress or a result to show, so a host
 * can mount it unconditionally.
 */
import { useCallback, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import api from '../../../services/api';
import DispositionReasonModal from '../../components/DispositionReasonModal';
import { fieldCls } from '../../components/filters';
import { MEMBERSHIP_LIFECYCLES, notifyStatsChanged, useTeam } from '../../lib';
import {
  applyBulkReason,
  buildBulkPlan,
  bulkResultLine,
  bulkRowResult,
  withRowOwner,
  type BulkAction,
  type BulkPlan,
  type BulkRowResult,
} from '../recruitLogic';
import type { RecruitRow, RecruitRowChangeHandler, RecruitRowUpdate, RecruitStudy } from '../types';

export interface BulkBarProps {
  /** The ticked rows, in the list's order. */
  selected: readonly RecruitRow[];
  /** The study the list is for. */
  study: Pick<RecruitStudy, 'id'>;
  /** Apply a landed write to the row on screen. */
  onRowChange: RecruitRowChangeHandler;
  /** Untick everything (the bar's Clear). */
  onClear: () => void;
  className?: string;
}

/** The "Unassigned" choice of Set recruiter (owner -> null); never a real user id. */
const UNASSIGNED_CHOICE = '__unassigned__';

interface Summary {
  line: string;
  /** One sentence per row that did not change. */
  misses: string[];
}

/** The row on screen after one landed write of `action`. */
function applyWrite(action: BulkAction, stamp: string): RecruitRowUpdate {
  if (action.kind === 'owner') {
    const owner = action.owner_user_id || null;
    return (r) => withRowOwner(r, owner);
  }
  const status = action.status;
  return (r) => ({ ...r, membership: { ...r.membership, status, status_changed_at: stamp } });
}

/** "Dana Reyes: randomized in Migraine, not changed". */
function missLine(name: string, result: BulkRowResult): string {
  if (result.outcome === 'refused') return `${name}: randomized in ${result.study_title || 'another study'}, not changed`;
  if (result.outcome === 'failed') return `${name}: ${result.message || 'the change failed'}, not changed`;
  return name;
}

const selectCls = `${fieldCls} min-h-[44px] w-auto cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0`;

export function BulkBar({ selected, study, onRowChange, onClear, className = '' }: BulkBarProps) {
  const { team } = useTeam();
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  // A status move waiting on the one reason for the batch.
  const [pending, setPending] = useState<BulkPlan | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const run = useCallback(async (plan: BulkPlan) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setSummary(null);
    const total = plan.writes.length;
    setProgress({ done: 0, total });
    const stamp = new Date().toISOString();
    const update = applyWrite(plan.action, stamp);
    const results: BulkRowResult[] = [];
    for (let i = 0; i < total; i += 1) {
      const write = plan.writes[i];
      let result: BulkRowResult;
      try {
        await api.patch(write.path, write.body);
        result = bulkRowResult(write.contact_id);
      } catch (e: unknown) {
        result = bulkRowResult(write.contact_id, e);
      }
      results.push(result);
      if (result.outcome === 'updated') onRowChange(write.contact_id, update);
      setProgress({ done: i + 1, total });
    }
    const names = new Map(plan.writes.map((w): [string, string] => [w.contact_id, w.name]));
    setSummary({
      line: bulkResultLine(results, plan.unchanged.length),
      misses: results
        .filter((r) => r.outcome !== 'updated')
        .map((r) => missLine(names.get(r.contact_id) || 'A lead', r)),
    });
    setProgress(null);
    busyRef.current = false;
    setBusy(false);
    if (results.some((r) => r.outcome === 'updated')) notifyStatsChanged(); // sidebar badges
  }, [onRowChange]);

  const start = (action: BulkAction) => {
    if (busyRef.current) return;
    // Only this study's rows: a per-study write never reaches another study's list.
    const rows = selectedRef.current.filter((r) => r.membership?.study_id === study.id);
    const plan = buildBulkPlan(rows, action);
    if (plan.writes.length === 0) {
      setSummary({ line: bulkResultLine([], plan.unchanged.length), misses: [] });
      return;
    }
    if (plan.requiresReason) {
      setPending(plan);
      return;
    }
    void run(plan);
  };

  const setRecruiter = (value: string) => {
    if (!value) return;
    start({ kind: 'owner', owner_user_id: value === UNASSIGNED_CHOICE ? null : value });
  };

  const setStatus = (value: string) => {
    if (!value) return;
    start({ kind: 'status', status: value });
  };

  const count = selected.length;
  if (count === 0 && !busy && !summary && !pending) return null;

  const pendingStatus = pending && pending.action.kind === 'status' ? pending.action.status : null;

  return (
    <div
      role="region"
      aria-label="Change the selected leads"
      className={`flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 p-2.5 dark:border-primary-800 dark:bg-primary-900/20 ${className}`}
    >
      {count > 0 && (
        <span className="text-sm font-medium text-primary-800 dark:text-primary-300">
          {count.toLocaleString()} selected
        </span>
      )}
      {count > 0 && (
        <>
          <select
            value=""
            disabled={busy}
            onChange={(e) => setRecruiter(e.target.value)}
            aria-label="Set the recruiter for the selected leads"
            className={selectCls}
          >
            <option value="">Set recruiter…</option>
            <option value={UNASSIGNED_CHOICE}>Unassigned</option>
            {team.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <select
            value=""
            disabled={busy}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Set the status on this study for the selected leads"
            className={selectCls}
          >
            <option value="">Set status…</option>
            {MEMBERSHIP_LIFECYCLES.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </>
      )}
      {busy && progress && (
        <span className="inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Updating {progress.done.toLocaleString()} of {progress.total.toLocaleString()}
        </span>
      )}
      {summary && !busy && (
        <div role="status" className="min-w-0 text-sm text-primary-800 dark:text-primary-300">
          <span className="font-medium">{summary.line}</span>
          {summary.misses.length > 0 && (
            <ul className="mt-0.5 list-disc pl-5 text-xs text-primary-900 dark:text-primary-200">
              {summary.misses.map((m, i) => (
                <li key={`${i}-${m}`}>{m}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="ml-auto flex items-center gap-1">
        {summary && !busy && (
          <button
            type="button"
            onClick={() => setSummary(null)}
            className="inline-flex min-h-[44px] items-center rounded-lg px-2 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-900/40 sm:min-h-0"
          >
            Dismiss
          </button>
        )}
        {count > 0 && !busy && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-900/40 sm:min-h-0"
          >
            <X className="h-4 w-4" aria-hidden="true" /> Clear
          </button>
        )}
      </div>
      {pending && pendingStatus && (
        <DispositionReasonModal
          status={pendingStatus}
          bulkCount={pending.writes.length}
          onCancel={() => setPending(null)}
          onConfirm={(reasonCode, note) => {
            const plan = applyBulkReason(pending, reasonCode, note);
            setPending(null);
            void run(plan);
          }}
        />
      )}
    </div>
  );
}

export default BulkBar;
